import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import Inlineloader from '../../../components/Loader/InlineLoader';
import FaqModal from '../../../components/Modals/FaqModal';
import TrackModal from '../../../components/Modals/TrackModal';
import CustomSearch from '../../../components/UI/CustomSearch';
import useDebounce from '../../../hooks/useDebounce';
import {
  createWarrantyUserBulk,
  deleteWarrantyUser,
  getSignedURL,
  getWarrantyProductCategory,
  getWarrantyProductSubCategory,
  getWarrantyStoreRetailer,
  getWarrantyType,
  getWarrantyUser,
  getWarrantyUserDownload,
} from '../../../services/Service';
import AddWarrantyModal from './AddWarrantyModal';
import DownloadMenu from './DownloadMenu';
import FilterMenu from './FilterMenu';
import './SafalWarrantyPage.css';
import TableView from './TableView';

const SafalWarrantyPage = () => {
  // const login = useSelector((state) => state.login);
  // const { registered } = login.user;
  const [selectedData, setSelectedData] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const [productCategoryData, setProductCategoryData] = useState([]);
  const [productSubCategoryData, setProductSubCategoryData] = useState([]);
  const [storeRetailerData, setStoreRetailerData] = useState([]);
  const [warrantyTypeData, setWarrantyTypeData] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [openChangeLogModal, setOpenChangeLogModal] = useState({
    open: false,
    data: null,
  });

  const [totalData, setTotalData] = useState(0);
  const [modalControl, setModalControl] = useState({
    open: false,
    data: null,
    isDisplay: false,
  });
  const [filter, setFilter] = useState({
    page: 1,
    limit: 15,
    search: '',
    order: '',
    orderBy: '',
    expireDateTo: '',
    expireDateForm: '',
    purchaseDateTo: '',
    purchaseDateForm: '',
    purchasePriceMin: '',
    purchasePriceMax: '',
    warrantyDetailsWarrantyTypeId: '', // true/false
    status: '', // Active/Inactive
  });

  const fetchData = async (newFilter, loadingFlag) => {
    try {
      if (!loadingFlag) setLoading(true);
      const res = await getWarrantyUser(newFilter);
      if (res.status === 200 && res.data.success === true) {
        setData(res.data.data);
        setTotalData(res.data.total ?? 0);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message);
    } finally {
      if (!loadingFlag) setLoading(false);
    }
  };

  const fetchExtra = () => {
    getWarrantyType().then((res) => setWarrantyTypeData(res.data.data || []));
    getWarrantyStoreRetailer().then((res) => setStoreRetailerData(res.data.data || []));
    getWarrantyProductSubCategory().then((res) => setProductSubCategoryData(res.data.data || []));
    getWarrantyProductCategory().then((res) => setProductCategoryData(res.data.data || []));
  };

  useEffect(() => {
    const redirectId = searchParams.get('redirectId');
    if (!data) return;
    if (!redirectId) return;
    if (redirectId?.length < 5) return;
    if (Array.isArray(data) && data.length > 0) {
      const findData = data.find((ex) => ex?._id === redirectId);
      if (findData) {
        setModalControl({
          open: true,
          data: findData,
          isDisplay: true,
        });
        setSearchParams((prev) => {
          prev.delete('redirectId');
          return prev;
        });
      } else {
        setSearchParams((prev) => {
          prev.delete('redirectId');
          return prev;
        });
      }
    }
    // handle redirect
  }, [data]);

  useEffect(() => {
    fetchData(filter);
  }, [filter]);

  useEffect(() => {
    fetchExtra();
  }, []);

  useDebounce(
    () => {
      if (filter.search !== searchValue) {
        setFilter((prev) => ({ ...prev, search: searchValue }));
      }
    },
    [searchValue],
    1500
  );

  const onFilterReset = () => {
    setFilter((prev) => ({
      ...prev,
      expireDateTo: '',
      expireDateForm: '',
      purchaseDateTo: '',
      purchaseDateForm: '',
      purchasePriceMin: '',
      purchasePriceMax: '',
      warrantyDetailsWarrantyTypeId: '',
      status: '', // Active/Inactive
    }));
  };
  const onFilterApply = (newFilterState) => {
    setFilter((prev) => ({
      ...prev,
      ...newFilterState,
    }));
  };
  const onSelectedChange = (id) => {
    setSelectedData((prev) => {
      if (prev.includes(id)) {
        return prev.filter((e) => e !== id);
      }
      return [...prev, id];
    });
  };
  const onPageChange = (_, value) => {
    setFilter((prev) => ({ ...prev, page: value + 1 }));
  };

  const onRowsPerPageChange = (e) => {
    setFilter((prev) => ({ ...prev, page: 1, limit: e.target.value }));
  };

  const onModalClose = (refetchFlag) => {
    setModalControl({
      open: false,
      data: null,
      isDisplay: false,
    });
    if (refetchFlag === true) refetch();
  };
  const onAdd = () => {
    setModalControl({
      open: true,
      data: null,
      isDisplay: false,
    });
  };
  const onEdit = (data) => {
    setModalControl({
      open: true,
      data: data,
      isDisplay: false,
    });
  };
  const onDelete = async (data) => {
    const result = await Swal.fire({
      text: 'Are you sure you want to delete this?',
      showCancelButton: true,
      confirmButtonColor: '#3D71FF',
      cancelButtonColor: '#ff4842',
      confirmButtonText: 'Yes',
      appendTo: 'body',
      customClass: {
        container: 'my-swal', // Add a custom class for styling
      },
      onBeforeOpen: () => {
        // Set a higher zIndex for the Swal modal
        document.querySelector('.my-swal').style.zIndex = 999999;
      },
    });
    if (!result.isConfirmed) {
      return;
    }
    try {
      const res = await deleteWarrantyUser({ id: data._id });
      if (res.status === 200) {
        toast.success(res.data.message);
        refetch();
      }
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || err.message);
    }
  };
  const onDisplay = (data) => {
    setModalControl({
      open: true,
      data: data,
      isDisplay: true,
    });
  };

  const refetch = () => {
    fetchData(filter);
  };

  // Change Log
  const onCloseChangeLog = () => {
    setOpenChangeLogModal({
      data: null,
      open: false,
    });
  };
  // Change Log
  const onOpenChangelog = (data) => {
    setOpenChangeLogModal({
      data: data,
      open: true,
    });
  };

  const downloadFile = async (type) => {
    try {
      const res = await getWarrantyUserDownload({ ...filter, fileType: type });
      const signedURLRes = await getSignedURL({
        url: res.data.url,
      });
      const link = document.createElement('a');
      link.href = signedURLRes.data;
      link.setAttribute('download', `file`);
      document.body.appendChild(link);
      link.click();

      toast.success('download successful');
    } catch (err) {
      const msg = err?.response?.data?.message || 'download failed, please try again';
      toast.error(msg);
    }
  };

  const handleBulkFileUpload = async (e) => {
    try {
      const file = e.target.files[0];
      if (file) {
        const res = await createWarrantyUserBulk(file);
        if (res.status === 200) {
          // toast.success(res.data.message);
          // refetch();
          const htmlStringSuc =
            res.data.successName?.length > 0
              ? `<b>Successfully Created</b><ul>${res.data.successName?.map((e) => `<li>${e}</li>`).join('')}</ul>`
              : '';
          const htmlStringErrorName =
            res.data.errorName?.length > 0
              ? `<b>Failed Created</b><ul>${res.data.errorName?.map((e) => `<li>${e}</li>`).join('')}</ul>`
              : '';
          const htmlString = `
            <div>
              ${htmlStringSuc}
              ${htmlStringErrorName}
            </div>
          `;

          const result = await Swal.fire({
            html: htmlString,
            confirmButtonColor: '#ff4842',
            confirmButtonText: 'Close',
            appendTo: 'body',
            customClass: {
              container: 'my-swal', // Add a custom class for styling
            },
            onBeforeOpen: () => {
              // Set a higher zIndex for the Swal modal
              document.querySelector('.my-swal').style.zIndex = 999999;
            },
          });

          if (result.isConfirmed) {
            refetch();
          }
        }
      }
    } catch (err) {
      console.log(err);
      toast.error(err?.response?.data?.message || err.message);
    }
  };

  if (loading) return <Inlineloader />;
  return (
    <>
      <Helmet>
        <title>Warranty - SafalSubscriptions</title>
      </Helmet>
      <div id="SafalWarrantyPage" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', flex: 'none', marginBottom: '8px' }}>
          <Typography variant="h4" sx={{ fontSize: '24px', fontWeight: 700, color: '#3D71FF' }}>
            Your Warranty
          </Typography>
          <FaqModal module="Warranty" color="#3D71FF" />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
            <div style={{ display: 'flex', gap: '8px' }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <CustomSearch
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search..."
              />
              <Tooltip
                title={
                  <>
                    {'Bulk Warranty Upload - Excel file'}
                    <br />
                    {
                      'Go To Profile > Settings > Templates Tab to use the template of Mass Upload along with detailed instructions'
                    }
                  </>
                }
              >
                <IconButton
                  sx={{
                    height: '40px',
                    flex: 'none',
                    color: '#FFFFFF',
                    backgroundColor: '#3D71FF',
                    '&:hover': { backgroundColor: '#3D71FF', color: '#FFFFFF' },
                  }}
                  onClick={() => {
                    const ele = document.getElementById('Bulk-Warranty-Upload');
                    if (ele) ele.click();
                  }}
                >
                  <CloudUploadIcon />
                </IconButton>
              </Tooltip>
              <input
                type="file"
                onChange={handleBulkFileUpload}
                style={{ display: 'none' }}
                id="Bulk-Warranty-Upload"
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              />
              <DownloadMenu downloadFile={downloadFile} />
              <FilterMenu
                warrantyTypeData={warrantyTypeData}
                filterState={filter}
                onFilterReset={onFilterReset}
                onFilterApply={onFilterApply}
              />
              <Tooltip title="Refresh">
                <IconButton
                  sx={{
                    height: '40px',
                    flex: 'none',
                    color: '#FFFFFF',
                    backgroundColor: '#3D71FF',
                    '&:hover': { backgroundColor: '#3D71FF', color: '#FFFFFF' },
                  }}
                  onClick={() => refetch()}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add new">
                <IconButton
                  onClick={onAdd}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#3D71FF',
                    height: '40px',
                    width: '40px',
                    '&:hover': { backgroundColor: '#3D71FF' },
                  }}
                >
                  <AddIcon sx={{ color: 'white' }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>
          <TableView
            selectedAccount={selectedData}
            onSelectedChange={onSelectedChange}
            onDisplay={onDisplay}
            onDelete={onDelete}
            onEdit={onEdit}
            onChangeLog={onOpenChangelog}
            data={data}
            paginationData={{
              total: totalData ?? 0,
              page: filter?.page ?? 1,
              limit: filter?.limit ?? 15,
            }}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
          />
        </div>
      </div>
      <AddWarrantyModal
        open={modalControl.open}
        onClose={onModalClose}
        isDisplay={modalControl.isDisplay}
        data={modalControl.data}
        productCategoryData={productCategoryData}
        productSubCategoryData={productSubCategoryData}
        storeRetailerData={storeRetailerData}
        warrantyTypeData={warrantyTypeData}
      />
      <TrackModal
        subName={openChangeLogModal?.data?.productName || 'Warranty'}
        openTrackModal={openChangeLogModal.open}
        moduleNameType="SafalWarranty"
        onClose={onCloseChangeLog}
        data={openChangeLogModal.data}
      />
    </>
  );
};

export default SafalWarrantyPage;
